import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { MouseEvent, useMemo, useRef, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'

import DynamicTextAutocomplete from '@common/components/DynamicTextAutocomplete'
import { DicomTagRecord, TagDeidStrategy } from '@common/config/api/client'
import { useStudiesMetadataOptions } from '@study_setup/hooks/useStudyMetadataOptions'
import { Checkbox } from '@common/components/Form/Checkbox'
import { Option } from '@common/components/DynamicTextAutocomplete/DynamicTextAutocomplete'
import { SIZING } from '@common/theme/sizing'
import { SPACING } from '@common/theme/spacing'
import { useDeleteDicomTag, useUpdateDicomTag } from '@features/tag_deid_management/hooks/useTagDeidQueries'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'
import { Switch } from '@common/components/Form/Switch'
import { isEmptyTagAllowed } from './emptyTagUtils'

type DICOMTagDeidRowProps = {
  tagId: string
  isOpen: boolean
  onToggleCollapse: () => void
  isGlobalTagDeidView?: boolean
}

const DICOMTagDeidRow = ({
  tagId,
  isOpen: modificationCollapseOpen,
  onToggleCollapse,
  isGlobalTagDeidView,
}: DICOMTagDeidRowProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const { control, setValue, getValues, watch } = useFormContext()
  const { mutate: deleteDicomTag } = useDeleteDicomTag()
  const { mutate: updateDicomTag } = useUpdateDicomTag()

  const { dynamicModifications } = useStudiesMetadataOptions()
  const tags = watch('tags')
  const tagIndex = useMemo(() => {
    return tags.findIndex((tag: DicomTagRecord) => tag.tag_id === tagId)
  }, [tagId])

  const tag = watch(`tags.${tagIndex}`)
  const {
    control: modificationControl,
    getValues: getModificationValues,
    setError,
  } = useForm({
    defaultValues: {
      modification: getValues(`tags.${tagIndex}.value`),
    },
  })
  const autocompleteRef = useRef<HTMLInputElement | null>(null)

  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  const handleDeleteIconClick = (dicom_tag_id: string) => {
    if (isGlobalTagDeidView) {
      deleteDicomTag(
        { dicom_tag_id: dicom_tag_id },
        {
          onSuccess: () => {
            toast(<Toast message="DICOM tag deleted successfully!" variant="success" />)
          },
          onError: (error) => {
            toast(
              <Toast message={(error?.detail && error.detail[0]?.msg) || 'Failed to delete tag.'} variant="error" />,
            )
          },
        },
      )
    }

    const updatedTags = tags.filter((_: DicomTagRecord, index: number) => index !== tagIndex)
    setValue('tags', updatedTags)
  }

  const handleModifyTag = () => {
    onToggleCollapse()
    handleMenuClose()
  }

  const persistGlobalStrategy = (strategy: TagDeidStrategy) => {
    if (!isGlobalTagDeidView || !tag?.value_representation?.id) return

    updateDicomTag(
      {
        dicom_tag_id: tag.id,
        dicom_data: {
          value: '',
          tag_deid_strategy: strategy,
          dicom_tag_id: tag.tag_id,
          dicom_tag_name: tag.tag_name,
          value_representation_id: tag.value_representation.id,
        },
      },
      {
        onSuccess: () => {
          toast(<Toast message="DICOM tag updated successfully!" variant="success" />)
        },
        onError: (error) => {
          toast(<Toast message={(error?.detail && error.detail[0]?.msg) || 'Failed to update tag.'} variant="error" />)
        },
      },
    )
  }

  const handleRemoveTag = () => {
    setValue(`tags.${tagIndex}.tag_deid_strategy`, 'REMOVE_TAG')
    setValue(`tags.${tagIndex}.value`, '')
    persistGlobalStrategy('REMOVE_TAG')
    handleMenuClose()
  }

  const handleEmptyTag = () => {
    setValue(`tags.${tagIndex}.tag_deid_strategy`, 'EMPTY_TAG')
    setValue(`tags.${tagIndex}.value`, '')
    persistGlobalStrategy('EMPTY_TAG')
    handleMenuClose()
  }

  const validateModificationFormat = (value: string, availableOptions: Option[]) => {
    if (!value.includes('{')) return true

    const matches = value.match(/\{([^}]+)\}/g)

    const openBraces = (value.match(/\{/g) || []).length
    const closeBraces = (value.match(/\}/g) || []).length
    if (openBraces !== closeBraces) return false

    if (!matches && openBraces > 0) return false

    if (matches) {
      return matches.every((match) => {
        const variable = match.slice(1, -1)
        return availableOptions.some((option) => option.label === variable)
      })
    }

    return true
  }
  const handleSaveClick = (tag: DicomTagRecord) => {
    const modificationValue = getModificationValues('modification')
    const hasValidFormat = validateModificationFormat(modificationValue, dynamicModifications)
    if (!hasValidFormat) {
      setError('modification', {
        type: 'manual',
        message: 'Invalid format. Use {} to wrap variables and ensure they match available options',
      })
      return
    }

    if (isGlobalTagDeidView) {
      updateDicomTag(
        {
          dicom_tag_id: tag.id,
          dicom_data: {
            value: modificationValue,
            tag_deid_strategy: modificationValue ? 'MODIFY_TAG' : 'UNCHANGE_TAG',
            dicom_tag_metadata_id: tag.id,
          },
        },
        {
          onSuccess: () => {
            toast(<Toast message="DICOM tag updated successfully!" variant="success" />)
          },
          onError: (error) => {
            toast(
              <Toast message={(error?.detail && error.detail[0]?.msg) || 'Failed to update tag.'} variant="error" />,
            )
          },
        },
      )
    }

    setValue(`tags.${tagIndex}.value`, modificationValue)
    setValue(`tags.${tagIndex}.tag_deid_strategy`, 'MODIFY_TAG')
    onToggleCollapse()
  }

  const handleUnModifyDicomTag = () => {
    setValue(`tags.${tagIndex}.value`, '')
    setValue(`tags.${tagIndex}.tag_deid_strategy`, 'UNCHANGE_TAG')
    handleMenuClose()
  }

  const mapTagDeidStrategyToTagProps = (tagDeidStrategy: TagDeidStrategy) => {
    switch (tagDeidStrategy) {
      case 'REMOVE_TAG':
        return { label: 'Removed', color: 'error-alt' }
      case 'MODIFY_TAG':
        return { label: 'Modified', color: 'warning-alt' }
      case 'EMPTY_TAG':
        return { label: 'Emptied', color: 'default' }
      case 'UNCHANGE_TAG':
        return { label: 'Unchanged', color: 'info-alt' }
      default:
        return { label: 'Unchanged', color: 'info-alt' }
    }
  }

  const canEmptyTag = isEmptyTagAllowed(tag?.tag_id)

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ maxWidth: SIZING.iconWidthMd, borderBottom: '1px solid transparent' }}>
          <IconButton aria-label="expand row" size="small" onClick={onToggleCollapse}>
            {modificationCollapseOpen ? (
              <ExpandLessRoundedIcon fontSize="small" />
            ) : (
              <ExpandMoreRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          {tag?.tag_id} - {tag?.tag_name}
        </TableCell>
        <TableCell>
          <Chip
            label={mapTagDeidStrategyToTagProps(tag?.tag_deid_strategy).label}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            color={mapTagDeidStrategyToTagProps(tag?.tag_deid_strategy).color}
            size="small"
          />
        </TableCell>
        <TableCell colSpan={1}>
          <Checkbox control={control} name={`tags.${tagIndex}.allow_iqm_override`} label="Allow QC to edit" />
        </TableCell>
        <TableCell>{tag?.value}</TableCell>
        <TableCell>
          <Switch
            control={control}
            color="primary"
            label=""
            name={`tags.${tagIndex}.allow_apply_to_all_series`}
            sx={{ transform: 'scale(0.85)' }}
          />
        </TableCell>
        <TableCell sx={{ width: SIZING.tableCellMenuWidth }}>
          <IconButton onClick={handleMenuClick}>
            <MoreVertRoundedIcon />
          </IconButton>
          <IconButton data-testid="delete-icon-0" onClick={() => handleDeleteIconClick(tag.id)}>
            <DeleteRoundedIcon />
          </IconButton>
          <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
            {tag?.tag_deid_strategy === 'UNCHANGE_TAG' ? (
              <MenuItem onClick={handleModifyTag}>Modify tag</MenuItem>
            ) : (
              <MenuItem onClick={handleUnModifyDicomTag}>Un-modify tag</MenuItem>
            )}
            <MenuItem onClick={handleRemoveTag}>
              Remove tag
              <Tooltip title="Tag will be removed if present in incoming image" placement="top">
                <IconButton size="small">
                  <InfoRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MenuItem>
            <MenuItem onClick={handleEmptyTag} disabled={!canEmptyTag}>
              Empty tag
              <Tooltip
                title={
                  canEmptyTag
                    ? 'Tag will be kept with an empty value if present in incoming image'
                    : 'Empty tag is not allowed for Pixel Data'
                }
                placement="top"
              >
                <IconButton size="small">
                  <InfoRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{
            paddingBottom: SPACING.noSpacing,
            paddingTop: SPACING.noSpacing,
          }}
          colSpan={7}
        >
          <Collapse in={modificationCollapseOpen} timeout="auto" unmountOnExit>
            <Box
              pl={SPACING.DICOMTagTableBoxSpacing}
              pr={SPACING.spacingLg}
              sx={{ bgcolor: 'secondary.light', padding: '16px' }}
            >
              <Typography my={SPACING.spacingMd} fontWeight={700} color="text.secondary" fontSize={14}>
                Modification
              </Typography>
              <Divider />
              <Box my={SPACING.spacingXl} display="flex" alignItems="center" gap={SPACING.spacingXl}>
                <DynamicTextAutocomplete
                  options={dynamicModifications}
                  sx={{ flexGrow: 1, bgcolor: 'white' }}
                  control={modificationControl}
                  name="modification"
                  inputRef={autocompleteRef}
                  textFieldProps={{
                    label: 'Modification',
                    size: 'medium',
                    sx: {
                      backgroundColor: 'secondary.light',
                      '& .MuiInputBase-root': {
                        backgroundColor: 'background.default',
                      },
                    },
                  }}
                />
                <Button variant="contained" size="large" onClick={() => handleSaveClick(tag)}>
                  Save
                </Button>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default DICOMTagDeidRow
